import { useState, useMemo } from 'react';
import { 
  History, 
  Eye, 
  Edit, 
  ArrowRight,
  Phone,
  Mail,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RotateCcw,
  Paperclip,
  MessageSquare,
  User,
  Clock,
  Download,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  Shield,
  FileText
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AuditLogEntry } from '@/types/ticket';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';

export type UserRole = 'agent' | 'supervisor' | 'manager' | 'admin' | 'auditor';

interface AuditTrailTableProps {
  entries: AuditLogEntry[];
  userRole?: UserRole;
  onExportCSV?: () => void;
  onExportPDF?: () => void;
}

// Define visibility rules per role
const ROLE_VISIBILITY: Record<UserRole, AuditLogEntry['action'][]> = {
  agent: ['created', 'status_change', 'assigned', 'note_added', 'attachment_added', 'resolved', 'closed'],
  supervisor: ['created', 'status_change', 'priority_change', 'assigned', 'escalated', 'note_added', 'attachment_added', 'call_initiated', 'email_sent', 'resolved', 'closed', 'reopened'],
  manager: ['created', 'status_change', 'priority_change', 'assigned', 'escalated', 'viewed', 'note_added', 'attachment_added', 'call_initiated', 'email_sent', 'resolved', 'closed', 'reopened', 'sla_paused', 'sla_resumed'],
  admin: ['created', 'status_change', 'priority_change', 'assigned', 'escalated', 'viewed', 'note_added', 'attachment_added', 'call_initiated', 'email_sent', 'resolved', 'closed', 'reopened', 'sla_paused', 'sla_resumed', 'auto_close_extended', 'auto_closed'],
  auditor: ['created', 'status_change', 'priority_change', 'assigned', 'escalated', 'viewed', 'note_added', 'attachment_added', 'call_initiated', 'email_sent', 'resolved', 'closed', 'reopened', 'sla_paused', 'sla_resumed', 'auto_close_extended', 'auto_closed'],
};

const ACTION_CATEGORIES = [
  { value: 'all', label: 'All Actions' },
  { value: 'lifecycle', label: 'Lifecycle Changes' },
  { value: 'assignment', label: 'Assignment' },
  { value: 'communication', label: 'Communication' },
  { value: 'sla', label: 'SLA Events' },
  { value: 'views', label: 'Views & Access' },
];

const getActionIcon = (action: AuditLogEntry['action']) => {
  switch (action) {
    case 'created': return <MessageSquare className="h-3.5 w-3.5" />;
    case 'status_change': return <Edit className="h-3.5 w-3.5" />;
    case 'priority_change': return <AlertTriangle className="h-3.5 w-3.5" />;
    case 'assigned': return <User className="h-3.5 w-3.5" />;
    case 'escalated': return <ArrowRight className="h-3.5 w-3.5" />;
    case 'viewed': return <Eye className="h-3.5 w-3.5" />;
    case 'attachment_added': return <Paperclip className="h-3.5 w-3.5" />;
    case 'note_added': return <MessageSquare className="h-3.5 w-3.5" />;
    case 'call_initiated': return <Phone className="h-3.5 w-3.5" />;
    case 'email_sent': return <Mail className="h-3.5 w-3.5" />;
    case 'reopened': return <RotateCcw className="h-3.5 w-3.5" />;
    case 'resolved': return <CheckCircle className="h-3.5 w-3.5" />;
    case 'closed': return <XCircle className="h-3.5 w-3.5" />;
    case 'sla_paused': return <Clock className="h-3.5 w-3.5" />;
    case 'sla_resumed': return <Clock className="h-3.5 w-3.5" />;
    case 'auto_close_extended': return <Clock className="h-3.5 w-3.5" />;
    case 'auto_closed': return <XCircle className="h-3.5 w-3.5" />;
    default: return <History className="h-3.5 w-3.5" />;
  }
};

const getActionLabel = (action: AuditLogEntry['action']): string => {
  const labels: Record<string, string> = {
    created: 'Ticket Created',
    status_change: 'Status Changed',
    priority_change: 'Priority Changed',
    assigned: 'Assignment Changed',
    escalated: 'Escalated',
    viewed: 'Ticket Viewed',
    attachment_added: 'Attachment Added',
    note_added: 'Note Added',
    call_initiated: 'Call Initiated',
    email_sent: 'Email Sent',
    reopened: 'Ticket Reopened',
    resolved: 'Ticket Resolved',
    closed: 'Ticket Closed',
    sla_paused: 'SLA Paused',
    sla_resumed: 'SLA Resumed',
    auto_close_extended: 'Auto-close Extended',
    auto_closed: 'Auto-closed',
  };
  return labels[action] || action;
};

const getActionCategory = (action: AuditLogEntry['action']): string => {
  if (['created', 'status_change', 'resolved', 'closed', 'reopened'].includes(action)) return 'lifecycle';
  if (['assigned', 'escalated'].includes(action)) return 'assignment';
  if (['call_initiated', 'email_sent', 'note_added'].includes(action)) return 'communication';
  if (['sla_paused', 'sla_resumed', 'auto_close_extended', 'auto_closed'].includes(action)) return 'sla';
  if (['viewed'].includes(action)) return 'views';
  return 'other';
};

const getActionBadgeColor = (action: AuditLogEntry['action']): string => {
  switch (action) {
    case 'resolved':
    case 'closed': return 'bg-success/10 text-success border-success/20';
    case 'escalated':
    case 'priority_change': return 'bg-warning/10 text-warning border-warning/20';
    case 'reopened': return 'bg-destructive/10 text-destructive border-destructive/20';
    case 'sla_paused':
    case 'sla_resumed': return 'bg-primary/10 text-primary border-primary/20';
    default: return 'bg-muted text-muted-foreground border-border';
  }
};

const ITEMS_PER_PAGE = 15;

export function AuditTrailTable({ 
  entries, 
  userRole = 'agent',
  onExportCSV,
  onExportPDF,
}: AuditTrailTableProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Filter entries based on role visibility
  const roleFilteredEntries = useMemo(() => {
    const allowedActions = ROLE_VISIBILITY[userRole] || ROLE_VISIBILITY.agent;
    return entries.filter(entry => allowedActions.includes(entry.action));
  }, [entries, userRole]);

  // Apply search and category filters
  const filteredEntries = useMemo(() => {
    return roleFilteredEntries.filter(entry => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!entry.actor.toLowerCase().includes(query) &&
            !getActionLabel(entry.action).toLowerCase().includes(query) &&
            !(entry.details?.toLowerCase().includes(query))) {
          return false;
        }
      }
      
      // Category filter
      if (categoryFilter !== 'all') {
        if (getActionCategory(entry.action) !== categoryFilter) {
          return false;
        }
      }
      
      return true;
    });
  }, [roleFilteredEntries, searchQuery, categoryFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredEntries.length / ITEMS_PER_PAGE);
  const paginatedEntries = filteredEntries.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleExportCSV = () => {
    // Generate CSV content
    const headers = ['Timestamp', 'Actor', 'Action', 'Details', 'Previous Value', 'New Value'];
    const rows = filteredEntries.map(entry => [
      entry.timestamp,
      entry.actor,
      getActionLabel(entry.action),
      entry.details || '',
      entry.previousValue || '',
      entry.newValue || '',
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Audit log exported', { description: `${filteredEntries.length} entries exported to CSV` });
    onExportCSV?.();
  };

  const handleExportPDF = () => {
    // In real app: generate PDF using a library like jsPDF
    toast.success('Generating PDF...', { description: 'Your report will be downloaded shortly' });
    onExportPDF?.();
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Audit Trail</h2>
            <p className="text-sm text-muted-foreground">
              {filteredEntries.length} of {entries.length} entries visible
              {userRole !== 'auditor' && ' (filtered by role)'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1">
            <User className="h-3 w-3" />
            {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
          </Badge>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={handleExportCSV}>
            <FileText className="h-4 w-4" />
            CSV
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={handleExportPDF}>
            <Download className="h-4 w-4" />
            PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by actor, action, or details..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-9"
          />
        </div>
        <Select 
          value={categoryFilter} 
          onValueChange={(v) => {
            setCategoryFilter(v);
            setCurrentPage(1);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACTION_CATEGORIES.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-[180px]">Timestamp</TableHead>
              <TableHead className="w-[150px]">Actor</TableHead>
              <TableHead className="w-[160px]">Action</TableHead>
              <TableHead>Details</TableHead>
              <TableHead className="w-[120px]">Previous</TableHead>
              <TableHead className="w-[120px]">New</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No audit entries found
                </TableCell>
              </TableRow>
            ) : (
              paginatedEntries.map((entry) => (
                <TableRow key={entry.id} className="hover:bg-muted/30">
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {entry.timestamp}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-medium text-foreground">{entry.actor}</span>
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant="outline" 
                      className={cn("gap-1.5 text-xs", getActionBadgeColor(entry.action))}
                    >
                      {getActionIcon(entry.action)}
                      {getActionLabel(entry.action)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                    {entry.details || '-'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {entry.previousValue || '-'}
                  </TableCell>
                  <TableCell className="text-sm text-foreground">
                    {entry.newValue || '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredEntries.length)} of {filteredEntries.length}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-foreground px-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Audit Disclaimer */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 border border-border">
        <Shield className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
        <div className="text-xs text-muted-foreground space-y-1">
          <p className="font-medium text-foreground">Immutable Audit Record</p>
          <p>
            All entries in this audit log are immutable and cannot be modified or deleted. 
            This log is compliant with SOC 2 Type II and HIPAA audit requirements. 
            For regulatory inquiries, contact compliance@company.com.
          </p>
        </div>
      </div>
    </div>
  );
}
